import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Modal } from '@landr/maestro';
import { useActivateTrialSubscription } from 'apollo/user';
import { ModalProps } from 'modals/Modal.types';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useStoreAttemptedDownloadAction } from 'hooks/useStoreAttemptedDownloadAction';
import { attemptedActionStore } from 'helpers/attemptedActionStore';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { analytics } from 'utils';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
    ModalName,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { SubscriptionDetailsStatus } from 'types/generated/graphql';
import { ActivateTrialSubscriptionModalContent } from './ActivateTrialSubscriptionModalContent';

const ActivateTrialSubscriptionModal: React.FC<ModalProps> = ({
    isOpen,
    handleClose,
}) => {
    const [isConfirmationLoading, setIsConfirmationLoading] = useState(false);
    const { cancelPurchase, item: downloadableItem } =
        usePurchaseControllerContext();
    const { activateTrialSubscription, loading, error, data } =
        useActivateTrialSubscription();
    const { subscriptionsDetails } =
        useSubscriptionsAndAllowedFeaturesContext();

    useStoreAttemptedDownloadAction(downloadableItem, isOpen);

    useEffect(() => {
        if (isOpen) {
            analytics.trackModalOpened({
                modalName: ModalName.TrialActivation,
            });
        }
    }, [isOpen]);

    const handleActivateNow = async () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ActivatePlan,
            interactionSource: InteractionSource.TrialActivationModal,
            ctaType: CtaType.Button,
        });
        await activateTrialSubscription();
    };

    const handleCloseModal = () => {
        cancelPurchase();
        if (data) {
            setIsConfirmationLoading(true);
            // reload to get the activated subscription credits and states.
            // add a timeout so we leave enough time for the process to be completed in the BE.
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } else {
            attemptedActionStore.remove();
            handleClose();
        }
    };

    const trialingSubscription = useMemo(() => {
        if (subscriptionsDetails) {
            const trialingSubscription = subscriptionsDetails.find(
                (subscription) =>
                    subscription.status === SubscriptionDetailsStatus.Trial &&
                    subscription.isTrialingSubscription,
            );

            if (trialingSubscription?.current) {
                return trialingSubscription;
            }
        }
    }, [subscriptionsDetails]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCloseModal}
            size="sm"
            isDismissible={!loading}
        >
            <Modal.Header />
            <Modal.Content>
                <Modal.Section pt="none">
                    <Flex
                        flexDirection="column"
                        alignItems="center"
                        textAlign="center"
                        gap="sm"
                    >
                        <ActivateTrialSubscriptionModalContent
                            loading={loading || isConfirmationLoading}
                            error={error}
                            data={data}
                            onClose={handleCloseModal}
                            onActivateNow={handleActivateNow}
                            trialingSubscription={trialingSubscription}
                        />
                    </Flex>
                </Modal.Section>
                <Modal.Footer />
            </Modal.Content>
        </Modal>
    );
};

export default ActivateTrialSubscriptionModal;
